import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { MessageCircle, Clock, ArrowRight } from "lucide-react";
import api from "../lib/api";
import { PhotoImg, StatusTag, GoldButton, GhostButton, SectionTitle } from "../components/common";
import { CounterModal } from "../components/OfferModals";
import { useAuth } from "../context/AuthContext";

function Timeline({ history }) {
  return (
    <div className="mt-3 space-y-2 border-l border-[#D4AF37]/20 pl-4">
      {history.map((h, i) => (
        <div key={i} className="relative">
          <span className="absolute -left-[22px] top-1 h-2.5 w-2.5 rounded-full bg-[#D4AF37]" />
          <div className="text-xs text-[#D8D2C9]">
            <span className="capitalize font-medium text-[#FBF8F3]">{h.type}</span>
            {h.amount != null && <span className="text-[#D4AF37]"> · ${h.amount}</span>}
            {h.message && <p className="text-[#9E978E] mt-0.5">"{h.message}"</p>}
          </div>
        </div>
      ))}
    </div>
  );
}

function OfferRow({ o, box, onAction, onCounter }) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const cp = o.counterparty || {};
  return (
    <div data-testid={`offer-row-${o.offer_id}`} className="rounded-2xl border border-white/8 bg-[#141113]/70 p-4 db-fade-up">
      <div className="flex items-center gap-4">
        <div className="h-16 w-16 rounded-xl overflow-hidden border border-white/10 shrink-0 cursor-pointer" onClick={() => navigate(`/profile/${cp.user_id}`)}>
          <PhotoImg photoId={cp.main_photo_id} fallbackInitial={cp.display_name?.[0]} className="w-full h-full object-cover" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="font-serif text-lg text-[#FBF8F3]">{cp.display_name}</h3>
            <StatusTag status={o.status} />
          </div>
          <p className="text-sm text-[#D4AF37] font-serif">${o.current_amount}{o.activity ? <span className="text-[#9E978E] font-sans text-xs"> · {o.activity}</span> : null}</p>
        </div>
      </div>

      {open && <Timeline history={o.history || []} />}
      <button onClick={() => setOpen((v) => !v)} className="text-[11px] text-[#9E978E] hover:text-[#D4AF37] mt-2">{open ? "Hide" : "View"} offer history</button>

      <div className="flex flex-wrap gap-2 mt-3">
        {o.can_act && (
          <>
            <GoldButton data-testid={`offer-accept-btn-${o.offer_id}`} onClick={() => onAction(o, "accept")} className="px-4 py-2 text-xs">Accept {o.role === "recipient" ? "Offer" : "Counter"}</GoldButton>
            <GhostButton data-testid={`offer-counter-btn-${o.offer_id}`} onClick={() => onCounter(o)} className="px-4 py-2 text-xs">Counter</GhostButton>
            <GhostButton data-testid={`offer-decline-btn-${o.offer_id}`} onClick={() => onAction(o, "decline")} className="px-4 py-2 text-xs">Decline</GhostButton>
          </>
        )}
        {box === "sent" && (o.status === "PENDING" || o.status === "COUNTERED") && !o.can_act && (
          <GhostButton onClick={() => onAction(o, "cancel")} className="px-4 py-2 text-xs">Cancel Offer</GhostButton>
        )}
        {box === "sent" && (o.status === "PENDING" || o.status === "COUNTERED") && !o.can_act && (
          <span className="text-xs text-[#9E978E] self-center flex items-center gap-1"><Clock size={13} /> Awaiting their response</span>
        )}
        {["ACCEPTED", "DATE_PLANNING", "DATE_CONFIRMED", "COMPLETED"].includes(o.status) && o.connection_id && (
          <GoldButton data-testid={`offer-message-btn-${o.offer_id}`} onClick={() => navigate(`/messages/${o.connection_id}`)} className="px-4 py-2 text-xs flex items-center gap-1.5"><MessageCircle size={14} /> Open Conversation</GoldButton>
        )}
      </div>
    </div>
  );
}

export default function Offers() {
  const [box, setBox] = useState("received");
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [counterOffer, setCounterOffer] = useState(null);

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get("/offers", { params: { box } }); setOffers(data.offers); }
    catch (e) { toast.error("Could not load offers"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [box]);

  const doAction = async (o, action) => {
    try {
      const { data } = await api.post(`/offers/${o.offer_id}/${action}`);
      toast.success(`Offer ${action}ed`);
      if (action === "accept" && data.connection_id) toast("Conversation unlocked", { description: "Open it to start messaging." });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Action failed"); }
  };

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8">
      <SectionTitle eyebrow="Your Marketplace" title={box === "received" ? "Received Offers" : "Your Offers"} className="mb-5" />
      <div className="flex rounded-full bg-[#0C0A0A]/60 p-1 mb-6 w-full sm:w-80">
        <button data-testid="offers-tab-received" onClick={() => setBox("received")} className={`flex-1 rounded-full py-2 text-sm font-medium ${box === "received" ? "bg-[#541224] text-[#FBF8F3]" : "text-[#9E978E]"}`}>Received</button>
        <button data-testid="offers-tab-sent" onClick={() => setBox("sent")} className={`flex-1 rounded-full py-2 text-sm font-medium ${box === "sent" ? "bg-[#541224] text-[#FBF8F3]" : "text-[#9E978E]"}`}>Sent</button>
      </div>

      {loading ? <div className="space-y-3">{Array.from({ length: 3 }).map((_, i) => <div key={i} className="h-28 rounded-2xl bg-[#141113] animate-pulse" />)}</div>
        : offers.length === 0 ? (
          <div className="text-center py-20 text-[#9E978E]">
            <p className="font-serif text-2xl text-[#D8D2C9]">{box === "received" ? "No offers yet" : "You haven't made any offers"}</p>
            <p className="mt-2 text-sm">{box === "received" ? "Complete your profile to receive date offers." : "Discover someone and make your move."}</p>
          </div>
        ) : (
          <div className="space-y-3">{offers.map((o) => <OfferRow key={o.offer_id} o={o} box={box} onAction={doAction} onCounter={setCounterOffer} />)}</div>
        )}

      {counterOffer && <CounterModal offer={counterOffer} onClose={() => setCounterOffer(null)} onDone={load} />}
    </div>
  );
}
